import S3Client from './s3Client'

const MIN_CHUNK_SIZE = 5242880
const FINAL_FLUSH_MAX_ATTEMPTS = 3
const FINAL_FLUSH_RETRY_DELAY_MS = 1000

const NOOP = () => {}

const delay = ms => new Promise(resolve => setTimeout(resolve, ms))

export default class VideoUploadClient extends S3Client {
  constructor(
    {
      newMultipartUploadPath,
      generateMultipartUploadPresignedUrlsPath,
      completeMultipartUploadPath,
      listFilesWithPrefixPath,
      basePath,
      blobType = { type: 'video/webm' },
    },
    callbacks = {},
  ) {
    super({ newMultipartUploadPath, generateMultipartUploadPresignedUrlsPath, completeMultipartUploadPath, listFilesWithPrefixPath, basePath })

    this.videoChunksCache = []
    this.latestSentVideoChunkIndex = null
    this.blobType = { ...blobType }
    this.finished = false
    // Serializes every S3 operation: parts upload strictly in append order
    // and completion can never overtake or interleave with a part upload.
    this.operationQueue = Promise.resolve()
    this.callbacks = {
      onChunkUploadFailed: callbacks.onChunkUploadFailed || NOOP,
      onUploadCompleted: callbacks.onUploadCompleted || NOOP,
      onUploadFailed: callbacks.onUploadFailed || NOOP,
    }
  }

  enqueueOperation(operation) {
    const run = this.operationQueue.then(operation)
    // Keep the chain usable after a rejected operation; the caller of this
    // enqueue still observes the rejection through `run`.
    this.operationQueue = run.then(NOOP, NOOP)
    return run
  }

  async appendVideoChunk(videoChunk) {
    if (this.finished) {
      console.warn('Ignored appended chunk: upload already finalized')
      return null
    }

    if (parseInt(videoChunk.size) === 0) {
      console.log('Ignored appended chunk because contains a size of 0')
      return null
    }

    // Synchronous push keeps chunks in arrival order even while a part
    // upload is in flight; the queue drains the cache afterwards.
    this.videoChunksCache.push(videoChunk)

    if (this.cachedBytes() > MIN_CHUNK_SIZE) {
      return this.enqueueOperation(() => this.uploadVideoChunkCache())
    }

    return null
  }

  // Idempotent: the first call wins, later calls (and late chunks) are ignored.
  // Always resolves — upload errors surface through the callbacks, so callers
  // can fire-and-forget from event handlers.
  async finishUploadingAndCompleteMultipartUpload() {
    if (this.finished) return null
    this.finished = true

    return this.enqueueOperation(async () => {
      // Nothing was ever recorded (stop raced the recorder startup):
      // there is no video to complete and no failure to report. The
      // backend sweeper aborts the empty multipart upload later.
      if (this.latestSentVideoChunkIndex === null && this.videoChunksCache.length === 0) {
        return null
      }

      try {
        console.log('Finishing uploading video chunks')
        await this.uploadVideoChunkCacheWithRetries()
      } catch (error) {
        console.log(error)
        this.callbacks.onUploadFailed({
          reason: 'final_chunk_upload_failed',
          error_name: error?.name || null,
          error_message: error?.message || null,
        })
        return null
      }

      if (!this.allPartsUploaded()) {
        console.log('Error: Not all parts have been uploaded')
        const uploadedParts = this.currentMultipartUpload?.uploadedParts || []
        this.callbacks.onUploadFailed({
          reason: 'incomplete_parts',
          parts_uploaded: uploadedParts.filter(Boolean).length,
          parts_expected: this.currentMultipartUpload.attemptedPartNumbers.size,
        })
        return null
      }

      try {
        const partsCount = this.latestSentVideoChunkIndex === null ? 0 : this.latestSentVideoChunkIndex + 1
        const response = await this.completeMultipartUpload()
        this.callbacks.onUploadCompleted({ parts_count: partsCount })
        return response
      } catch (error) {
        this.callbacks.onUploadFailed({
          reason: 'complete_multipart_failed',
          error_name: error?.name || null,
          error_message: error?.message || null,
        })
        return null
      }
    })
  }

  // The final flush is the last chance to persist the buffered tail — a
  // transient network error here used to truncate the video, so retry.
  async uploadVideoChunkCacheWithRetries() {
    for (let attempt = 1; ; attempt++) {
      try {
        return await this.uploadVideoChunkCache({ final: true })
      } catch (error) {
        if (attempt >= FINAL_FLUSH_MAX_ATTEMPTS) throw error
        await delay(FINAL_FLUSH_RETRY_DELAY_MS * attempt)
      }
    }
  }

  cachedBytes() {
    return this.videoChunksCache.reduce((sizeAccumulator, chunk) => sizeAccumulator + parseInt(chunk.size), 0)
  }

  // Only ever runs inside the operation queue. Takes ownership of the cached
  // chunks synchronously, so chunks appended while the part is in flight
  // accumulate separately instead of being wiped when this upload lands. On
  // failure the chunks are put back in front so the next attempt re-sends
  // the same part number with the accumulated data, in order.
  async uploadVideoChunkCache({ final = false } = {}) {
    if (this.videoChunksCache.length === 0) return null

    // S3 rejects a completed upload whose non-final part is under 5 MiB.
    if (!final && this.cachedBytes() < MIN_CHUNK_SIZE) return null

    const chunksToUpload = this.videoChunksCache
    this.videoChunksCache = []

    const videoChunkCacheBlob = new Blob(chunksToUpload, this.blobType)
    const chunkIndex = this.latestSentVideoChunkIndex === null ? 0 : this.latestSentVideoChunkIndex + 1

    console.log('Uploading chunk with index %s and with size of %s', chunkIndex, videoChunkCacheBlob.size)

    let response = null
    try {
      response = await this.uploadPart(videoChunkCacheBlob, chunkIndex)
    } catch (error) {
      this.videoChunksCache = chunksToUpload.concat(this.videoChunksCache)
      this.callbacks.onChunkUploadFailed({
        part_number: chunkIndex + 1,
        http_status: error?.httpStatus || error?.response?.status || null,
        error_name: error?.name || null,
        error_message: error?.message || null,
      })
      throw error
    }

    console.log('Uploaded chunk with index %s and with size of %s', chunkIndex, videoChunkCacheBlob.size)

    this.latestSentVideoChunkIndex = chunkIndex

    return response
  }
}
